// Offline support for field mode: an IndexedDB "outbox" of queued writes (notes, photos,
// start, complete) plus a sync routine that replays them when back online. The service
// worker (public/sw.js) handles offline reads of the app shell and job data.
import { api } from '../api/client';

const DB_NAME = 'flightdeck-field';
const STORE = 'outbox';

export function uid() {
  try { return crypto.randomUUID(); } catch { return 'id-' + Date.now() + '-' + Math.random().toString(16).slice(2); }
}

let dbp;
function db() {
  if (!dbp) {
    dbp = new Promise((resolve, reject) => {
      const r = indexedDB.open(DB_NAME, 1);
      r.onupgradeneeded = () => { const d = r.result; if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE, { keyPath: 'id' }); };
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  }
  return dbp;
}
function store(mode) { return db().then((d) => d.transaction(STORE, mode).objectStore(STORE)); }
function reqp(r) { return new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); }); }

export async function enqueue(action) {
  const s = await store('readwrite');
  await reqp(s.add(action));
  notify();
}
export async function allPending() {
  const s = await store('readonly');
  const all = (await reqp(s.getAll())) || [];
  return all.sort((a, b) => a.createdAt - b.createdAt);
}
export async function pendingForJob(jobId) {
  return (await allPending()).filter((a) => String(a.jobId) === String(jobId));
}
async function removeAction(id) {
  const s = await store('readwrite');
  await reqp(s.delete(id));
  notify();
}
export async function pendingCount() {
  try { const s = await store('readonly'); return await reqp(s.count()); } catch { return 0; }
}

// --- pending-count subscribers (for the offline banner) ---
const subs = new Set();
export function onPendingChange(cb) { subs.add(cb); return () => subs.delete(cb); }
function notify() { pendingCount().then((n) => subs.forEach((cb) => cb(n))).catch(() => {}); }

// --- sync: replay the outbox in order ---
let syncing = false;
export async function syncNow() {
  if (syncing || !navigator.onLine) return { flushed: 0, remaining: await pendingCount() };
  syncing = true;
  let flushed = 0;
  try {
    const queue = await allPending();
    for (const a of queue) {
      try {
        await api(a.url, { method: a.method || 'POST', body: a.body });
        await removeAction(a.id);
        flushed += 1;
      } catch (e) {
        if (e && e.status) {
          // Server was reached but rejected (e.g. validation/expired session).
          // Drop it so one bad action can't block the whole queue forever.
          await removeAction(a.id);
        } else {
          // Network error — stop and retry on the next online event.
          break;
        }
      }
    }
  } finally {
    syncing = false;
  }
  return { flushed, remaining: await pendingCount() };
}

export function registerFieldSW() {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => { /* offline mode just won't be available */ });
}
